/**
 * 📋 useAcoesRibbon — Hook de configuração do Ribbon para Central de Ações
 *
 * Grupo "Ação" (left):
 *   [Nova Ação]  [Arquivo ▾]
 *
 * Arquivo ▾ dropdown:
 *   Novo Plano
 *   Abrir
 *   Salvar
 *   Salvar como .wf
 *   ─────────────────
 *   Importar        ▶  Importar Excel / Baixar Template
 *   Exportar PDF    ▶  Lista / Kanban / Matriz / Dashboard
 *   Exportar Imagem ▶  Lista / Kanban / Matriz / Dashboard
 *
 * Grupo "Visualização" (center): Lista | Kanban | Matriz | Dashboard
 * Grupo "Densidade"   (center, Kanban only): 1col | 2col | 3col
 */

import { useMemo } from 'react';
import {
  FileDown, FolderOpen, Save, SaveAll,
  FileSpreadsheet, FileText, Image as ImageIcon, ClipboardList, Folder,
  List, PanelsTopLeft, Grid3X3, BarChart3,
  RectangleVertical, Columns2, Columns3,
  LayoutList, LayoutDashboard, TableProperties,
  SlidersHorizontal, FilePlus,
} from 'lucide-react';
import type { RibbonConfig } from '../types/ribbon';

const NOOP = () => {};

export type AcoesViewMode = 'list' | 'kanban' | 'matrix' | 'dashboard';

export interface AcoesRibbonDeps {
  onNewAction?: () => void;
  onNewPlan?: () => void;
  onEditPlan?: () => void;
  onOpenLocal?: () => void;
  onSaveLocal?: () => void;
  onSaveAs?: () => void;
  onImportExcel?: () => void;
  onDownloadTemplate?: () => void;
  onExportPDF?: (type?: AcoesViewMode) => void;
  onExportImage?: (type?: AcoesViewMode) => void;
  activeView: AcoesViewMode;
  onNavigateList?: () => void;
  onNavigateKanban?: () => void;
  onNavigateMatrix?: () => void;
  onNavigateDashboard?: () => void;
  kanbanDensity?: '1col' | '2col' | '3col';
  onKanbanDensityChange?: (density: '1col' | '2col' | '3col') => void;
  filtersWidget?: React.ReactNode;
  /** Whether a plan or project is currently open — gates Nova Ação */
  isPlanSelected?: boolean;
  /** Whether the active context is an editable plan — gates Propriedades do Plano */
  canEditPlan?: boolean;
}

export function useAcoesRibbon(deps: AcoesRibbonDeps): RibbonConfig {
  const isKanban = deps.activeView === 'kanban';
  const canEditPlan = deps.canEditPlan === true;

  return useMemo<RibbonConfig>(() => ({
    moduleId: 'planning',
    groups: [
      // ─── Ação (LEFT) ─────────────────────────────
      {
        id: 'acao',
        label: 'Ação',
        position: 'left',
        actions: [
          {
            id: 'nova-acao',
            label: 'Ação',
            icon: FilePlus,
            onClick: deps.onNewAction ?? NOOP,
            disabled: !deps.isPlanSelected,
          },
          {
            id: 'arquivo',
            label: 'Arquivo',
            icon: Folder,
            onClick: NOOP,
            dropdown: [
              {
                id: 'edit-plan',
                label: 'Propriedades do Plano...',
                icon: SlidersHorizontal,
                onClick: deps.onEditPlan ?? NOOP,
                disabled: !canEditPlan,
              },
              {
                id: 'new-plan',
                label: 'Novo Plano',
                icon: ClipboardList,
                onClick: deps.onNewPlan ?? NOOP,
              },
              { id: 'div1', label: '', onClick: NOOP, divider: true },
              {
                id: 'open-local',
                label: 'Abrir',
                icon: FolderOpen,
                onClick: deps.onOpenLocal ?? NOOP,
              },
              {
                id: 'save-local',
                label: 'Salvar',
                icon: Save,
                onClick: deps.onSaveLocal ?? NOOP,
              },
              {
                id: 'save-as',
                label: 'Salvar como .wf',
                icon: SaveAll,
                onClick: deps.onSaveAs ?? NOOP,
              },
              { id: 'div2', label: '', onClick: NOOP, divider: true },
              // ── Importar (flyout) ──────────────────
              {
                id: 'importar',
                label: 'Importar',
                icon: FileSpreadsheet,
                onClick: NOOP,
                children: [
                  {
                    id: 'import-excel',
                    label: 'Importar Excel',
                    icon: FileSpreadsheet,
                    onClick: deps.onImportExcel ?? NOOP,
                  },
                  {
                    id: 'download-tpl',
                    label: 'Baixar Template',
                    icon: FileDown,
                    onClick: deps.onDownloadTemplate ?? NOOP,
                  },
                ],
              },
              // ── Exportar PDF (flyout) ──────────────
              {
                id: 'exportar-pdf',
                label: 'Exportar PDF',
                icon: FileText,
                onClick: NOOP,
                children: [
                  { id: 'pdf-list',      label: 'Lista',     icon: LayoutList,      onClick: () => deps.onExportPDF?.('list') },
                  { id: 'pdf-kanban',    label: 'Kanban',    icon: PanelsTopLeft,   onClick: () => deps.onExportPDF?.('kanban') },
                  { id: 'pdf-matrix',    label: 'Matriz',    icon: TableProperties, onClick: () => deps.onExportPDF?.('matrix') },
                  { id: 'pdf-dashboard', label: 'Dashboard', icon: LayoutDashboard, onClick: () => deps.onExportPDF?.('dashboard') },
                ],
              },
              // ── Exportar Imagem (flyout) ───────────
              {
                id: 'exportar-imagem',
                label: 'Exportar Imagem',
                icon: ImageIcon,
                onClick: NOOP,
                children: [
                  { id: 'png-list',      label: 'Lista',     icon: LayoutList,      onClick: () => deps.onExportImage?.('list') },
                  { id: 'png-kanban',    label: 'Kanban',    icon: PanelsTopLeft,   onClick: () => deps.onExportImage?.('kanban') },
                  { id: 'png-matrix',    label: 'Matriz',    icon: TableProperties, onClick: () => deps.onExportImage?.('matrix') },
                  { id: 'png-dashboard', label: 'Dashboard', icon: LayoutDashboard, onClick: () => deps.onExportImage?.('dashboard') },
                ],
              },
            ],
          },
        ],
      },
      // ─── Visualização (CENTER) ──────────────────
      {
        id: 'visualizacao',
        label: 'Visualização',
        position: 'center',
        actions: [
          { id: 'view-list',      label: 'Lista',     icon: List,          onClick: deps.onNavigateList      ?? NOOP, active: deps.activeView === 'list' },
          { id: 'view-kanban',    label: 'Kanban',    icon: PanelsTopLeft, onClick: deps.onNavigateKanban    ?? NOOP, active: deps.activeView === 'kanban' },
          { id: 'view-matrix',    label: 'Matriz',    icon: Grid3X3,       onClick: deps.onNavigateMatrix    ?? NOOP, active: deps.activeView === 'matrix' },
          { id: 'view-dashboard', label: 'Dashboard', icon: BarChart3,     onClick: deps.onNavigateDashboard ?? NOOP, active: deps.activeView === 'dashboard' },
        ],
      },
      // ─── Densidade (CENTER) — Kanban only ───────
      {
        id: 'densidade',
        label: 'Densidade',
        position: 'center',
        hidden: !isKanban,
        actions: [
          { id: 'density-1', label: '1 Coluna',  icon: RectangleVertical, onClick: () => deps.onKanbanDensityChange?.('1col'), active: deps.kanbanDensity === '1col' },
          { id: 'density-2', label: '2 Colunas', icon: Columns2,          onClick: () => deps.onKanbanDensityChange?.('2col'), active: deps.kanbanDensity === '2col' },
          { id: 'density-3', label: '3 Colunas', icon: Columns3,          onClick: () => deps.onKanbanDensityChange?.('3col'), active: deps.kanbanDensity === '3col' },
        ],
      },
    ],
    slots: {
      afterGroups: deps.filtersWidget,
    },
  }), [deps, isKanban]);
}
